import styled, { css } from "styled-components";

/* ─── Page shell ──────────────────────────────────────────── */
export const PageWrap = styled.div`
	padding: 28px 32px 40px;
	max-width: 960px;
`;

export const PageHeader = styled.div`
	margin-bottom: 28px;
`;

export const PageTitle = styled.h2`
	margin: 0 0 4px;
	font-size: 1.35rem;
	font-weight: 700;
	color: #0f172a;
`;

export const PageSubtitle = styled.p`
	margin: 0;
	font-size: 0.85rem;
	color: #64748b;
	line-height: 1.5;
`;

/* ─── Segmented control (top-level nav) ───────────────────── */
export const SegmentedWrap = styled.div`
	display: inline-flex;
	background: #f1f5f9;
	border-radius: 10px;
	padding: 3px;
	margin-bottom: 24px;
`;

export const SegmentedBtn = styled.button<{ active: boolean }>`
	all: unset;
	cursor: pointer;
	padding: 7px 20px;
	border-radius: 8px;
	font-size: 0.84rem;
	font-weight: 600;
	color: #64748b;
	transition: all 0.15s ease;
	${({ active }) =>
		active &&
		css`
			background: #fff;
			color: #0f172a;
			box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
		`}
	&:hover {
		color: #0f172a;
	}
`;

/* ─── Section card ────────────────────────────────────────── */
export const SectionCard = styled.div`
	background: #fff;
	border: 1px solid #e2e8f0;
	border-radius: 12px;
	overflow: hidden;
`;

export const SectionHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 16px 20px;
	border-bottom: 1px solid #f1f5f9;
`;

export const SectionTitle = styled.h3`
	margin: 0;
	font-size: 0.92rem;
	font-weight: 600;
	color: #334155;
`;

/* ─── Sub-nav pills (Customer / Station / booking context) ── */
export const PillRow = styled.div`
	display: flex;
	align-items: center;
	gap: 6px;
	flex-wrap: wrap;
`;

export const PillBtn = styled.button<{ active: boolean }>`
	all: unset;
	cursor: pointer;
	padding: 5px 14px;
	border-radius: 99px;
	font-size: 0.78rem;
	font-weight: 600;
	transition: all 0.15s ease;
	${({ active }) =>
		active
			? css`
					background: #1e40af;
					color: #fff;
			  `
			: css`
					background: #f1f5f9;
					color: #475569;
					&:hover {
						background: #e2e8f0;
					}
			  `}
`;

export const PillDivider = styled.span`
	width: 1px;
	height: 16px;
	background: #cbd5e1;
	margin: 0 4px;
`;

/* ─── Table ───────────────────────────────────────────────── */
export const TableWrap = styled.div`
	overflow-x: auto;
`;

export const StyledTable = styled.table`
	width: 100%;
	border-collapse: collapse;
	font-size: 0.84rem;

	th {
		text-align: left;
		padding: 10px 16px;
		font-weight: 600;
		font-size: 0.75rem;
		text-transform: uppercase;
		letter-spacing: 0.04em;
		color: #64748b;
		background: #f8fafc;
		border-bottom: 1px solid #e2e8f0;
		white-space: nowrap;
	}

	td {
		padding: 10px 16px;
		color: #334155;
		border-bottom: 1px solid #f1f5f9;
		vertical-align: middle;
	}

	tbody tr:hover {
		background: #f8fafc;
	}

	tbody tr:last-child td {
		border-bottom: none;
	}
`;

export const EmptyRow = styled.td`
	text-align: center;
	color: #94a3b8 !important;
	padding: 32px 16px !important;
	font-size: 0.85rem;
`;

export const CodeCell = styled.code`
	font-size: 0.78rem;
	background: #f1f5f9;
	padding: 2px 8px;
	border-radius: 4px;
	color: #475569;
`;

/* ─── Badge pills ─────────────────────────────────────────── */
export const BadgePill = styled.span<{ active?: boolean; color?: string }>`
	display: inline-block;
	padding: 2px 10px;
	border-radius: 9999px;
	font-size: 0.73rem;
	font-weight: 600;
	${({ color }) =>
		color
			? css`
					background: ${color}18;
					color: ${color};
					border: 1px solid ${color}30;
			  `
			: css`
					background: #f1f5f9;
					color: #64748b;
			  `}
`;

export const ActiveDot = styled.span<{ on: boolean }>`
	display: inline-block;
	width: 8px;
	height: 8px;
	border-radius: 50%;
	background: ${({ on }) => (on ? "#22c55e" : "#cbd5e1")};
`;

/* ─── Color swatch (priority) ─────────────────────────────── */
export const ColorSwatch = styled.span<{ hex: string }>`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	font-size: 0.8rem;
	color: #475569;

	&::before {
		content: "";
		display: inline-block;
		width: 14px;
		height: 14px;
		border-radius: 4px;
		background: ${({ hex }) => hex};
		border: 1px solid rgba(0, 0, 0, 0.1);
		flex-shrink: 0;
	}
`;

/* ─── Action row & buttons ────────────────────────────────── */
export const ActionRow = styled.div`
	display: flex;
	gap: 8px;
	justify-content: flex-end;
	margin-bottom: 16px;
`;

export const IconBtn = styled.button`
	all: unset;
	cursor: pointer;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 30px;
	height: 30px;
	border-radius: 6px;
	color: #64748b;
	transition: all 0.15s;

	&:hover {
		background: #f1f5f9;
		color: #0f172a;
	}

	&.danger:hover {
		background: #fef2f2;
		color: #dc2626;
	}
`;

/* ─── Error alert ─────────────────────────────────────────── */
export const ErrorAlert = styled.div`
	padding: 10px 14px;
	margin-bottom: 16px;
	background: #fef2f2;
	border: 1px solid #fecaca;
	border-radius: 8px;
	color: #dc2626;
	font-size: 0.84rem;
`;
